/**
 * The shared dark-room viewer for photo surfaces outside Walk detail.
 *
 * Walk detail owns the richer version: its response carries an editable
 * description, comments, a download ticket, and the walker's permissions.
 * List and record projections intentionally carry less than that, but a
 * thumbnail is still evidence and must open at a useful size. This component
 * gives those projections the same full-screen reading room without inventing
 * capabilities their API response cannot support.
 */

import { PhotoImage } from "./PhotoImage";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { downloadFile } from "../lib/photoTransfer";
import { useMediaQuery } from "../shell/useMediaQuery";
import { C } from "../tokens";
import { clockOf, dayLabel, META } from "./chrome";

export interface FullscreenPhoto {
  id: string;
  url?: string | null;
  thumbnailUrl?: string | null;
  description?: string | null;
  caption?: string | null;
  capturedAt?: string | null;
  walkName?: string | null;
  siteName?: string | null;
  /** Where the photo was taken. `path` is the Procore hierarchy ("Tower B ›
   *  Level 3"), `name` the node ("Level 3 · East Wing"). */
  location?: { name: string; path?: string } | null;
}

/** `Tower B › Level 3 › Level 3 · East Wing`, or just the node when Procore
 *  gave no hierarchy. The path is not repeated when it already ends with the
 *  node's own name, which is how a one-level project's locations arrive. */
export function locationLabel(location: { name: string; path?: string }): string {
  const path = location.path?.trim();
  const name = location.name.trim();
  if (!path) return name;
  if (path === name || path.endsWith(`› ${name}`) || path.endsWith(`> ${name}`)) return path;
  return `${path} › ${name}`;
}

function sourceOf(photo: FullscreenPhoto): string | null {
  return photo.url ?? photo.thumbnailUrl ?? null;
}

function suggestedName(photo: FullscreenPhoto, index: number): string {
  // `fileName` used to lead here and every caller set it to the caption or the
  // description, so this is the same name it always produced — see the
  // `File name` note in `facts` below.
  const stem = photo.caption ?? photo.description ?? `photo-${index + 1}`;
  return /\.[a-z0-9]{2,5}$/i.test(stem) ? stem : `${stem}.jpg`;
}

/**
 * Opens a projection-backed photo in the same dark, full-screen layout as
 * the Walk detail viewer. The `photos` list is deliberately retained so a
 * gallery or an evidence strip still has its expected previous/next flow.
 */
export function PhotoFullscreenViewer({
  photos,
  index,
  onStep,
  onClose,
  downloadUrl,
}: {
  photos: FullscreenPhoto[];
  index: number;
  onStep: (next: number) => void;
  onClose: () => void;
  /** Authenticated, same-origin file endpoint supplied by the owning surface. */
  downloadUrl?: string;
}) {
  const photo = photos[index];
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const downloadAttempt = useRef(0);
  /** Whether the pointer went DOWN on the stage itself — see the stage's own
   *  note on why the click alone is not enough. */
  const stageDown = useRef(false);
  const narrow = useMediaQuery("(max-width: 820px)");

  useEffect(() => {
    setDownloadFailed(false);
    setDownloading(false);
    // Ignore an old download's result after stepping to another photo/closing.
    downloadAttempt.current++;
    return () => { downloadAttempt.current++; };
  }, [photo, downloadUrl]);

  useEffect(() => {
    if (!photo) return;
    const previous = () => onStep((index + photos.length - 1) % photos.length);
    const next = () => onStep((index + 1) % photos.length);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (photos.length > 1 && event.key === "ArrowLeft") previous();
      if (photos.length > 1 && event.key === "ArrowRight") next();
    };
    addEventListener("keydown", onKey);
    const heldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      removeEventListener("keydown", onKey);
      document.body.style.overflow = heldOverflow;
    };
  }, [index, onClose, onStep, photo, photos.length]);

  if (!photo) return null;
  const source = sourceOf(photo);
  const downloadSource = downloadUrl ?? source;
  const previous = () => onStep((index + photos.length - 1) % photos.length);
  const next = () => onStep((index + 1) % photos.length);
  const facts = [
    photo.capturedAt
      ? { label: "Taken on", value: `${dayLabel(photo.capturedAt)} · ${clockOf(new Date(photo.capturedAt))}` }
      : null,
    photo.walkName ? { label: "Walk", value: photo.walkName } : null,
    photo.siteName ? { label: "Project", value: photo.siteName } : null,
    /*
      WHERE it was taken, in Procore's own terms.

      This replaced `File name`, which was never a file name: every caller set
      it to the caption or the description, so the panel printed the same
      sentence twice — once under DESCRIPTION and again under a label claiming
      it was something else (user, 2026-09-10).

      The value is the photo's own `location`, the same field the walk's photo
      panel reads, and the hierarchy leads: "Tower B › Level 3 › Level 3 ·
      East Wing" says where in the project this is, where the leaf alone says
      only what it is called. A photo with no location of its own gets NO row
      — the walk's location is not borrowed for it, because a walk crosses
      several and inheriting one would put a plausible wrong answer where an
      honest absence belongs.
    */
    photo.location ? { label: "Location", value: locationLabel(photo.location) } : null,
  ].filter((fact): fact is { label: string; value: string } => !!fact);

  return createPortal(
    <div
      data-testid="photo-fullscreen-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${photos.length}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: narrow ? "column" : "row",
        background: C.viewerBg,
        color: C.surface,
      }}
    >
      {/*
        The ground around the photo is a way out.
        
        Clicking past the edge of an image to dismiss it is what every viewer
        does, and this one only answered Escape and a button in the corner —
        so the obvious gesture did nothing and the reader learned the page was
        unresponsive rather than that they had missed.
        
        `target === currentTarget` is what keeps it from firing on the photo,
        the arrows or the counter: those are children, and a click on them has
        its own target. The `mousedown` guard is the other half — a click
        event's target is the common ancestor of where the button went down
        and came up, so dragging from the image out onto the ground would
        otherwise dismiss the photo the reader was pointing at.
      */}
      <div
        data-testid="photo-viewer-stage"
        onMouseDown={(event) => {
          stageDown.current = event.target === event.currentTarget;
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          if (!stageDown.current) return;
          onClose();
        }}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/*
          The mark is DRAWN, not typed.
          
          It was the character `\u2715` inside the label's own span, so it
          inherited the meta face and its 1.3px letter-spacing: a glyph whose
          weight, size and optical centre were all decided by a rule written
          for small-caps text. It sat high, read thin against the label beside
          it, and drifted a little further off with every font fallback. Two
          strokes at the icon weight the rest of the console uses put it back
          on the label's optical centre and keep it there.
        */}
        <button
          className="kb wvbtn"
          data-testid="photo-viewer-close"
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute",
            left: 22,
            top: 20,
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 36,
            padding: "0 15px 0 13px",
            borderRadius: 999,
            color: C.surface,
            zIndex: 1,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path
              d="M2.5 2.5l7 7M9.5 2.5l-7 7"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
          <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.3px", color: "inherit" }}>
            Close
          </span>
        </button>
        <span
          style={{
            ...META,
            position: "absolute",
            top: 28,
            left: 0,
            right: 0,
            textAlign: "center",
            fontSize: 11.5,
            letterSpacing: "1.4px",
            color: "rgba(255,255,255,0.45)",
            // It spans the full width of the stage. Left clickable it would
            // be a silent dead strip across the top of the one gesture this
            // view just gained.
            pointerEvents: "none",
          }}
        >
          {`${index + 1} / ${photos.length}`}
        </span>
        {photos.length > 1 && (
          <>
            <button
              className="kb wvbtn"
              data-testid="photo-viewer-prev"
              aria-label="Previous photo"
              onClick={previous}
              style={{ position: "absolute", left: 24, top: "50%", marginTop: -24, width: 48, height: 48, borderRadius: 24, color: C.surface, zIndex: 1, fontSize: 18.4 }}
            >
              ‹
            </button>
            <button
              className="kb wvbtn"
              data-testid="photo-viewer-next"
              aria-label="Next photo"
              onClick={next}
              style={{ position: "absolute", right: 24, top: "50%", marginTop: -24, width: 48, height: 48, borderRadius: 24, color: C.surface, zIndex: 1, fontSize: 18.4 }}
            >
              ›
            </button>
          </>
        )}
        {source ? (
          <PhotoImage
            src={source}
            alt={photo.description ?? ""}
            style={{ maxWidth: narrow ? "calc(100% - 32px)" : "calc(100% - 144px)", maxHeight: narrow ? "54vh" : "86vh", objectFit: "contain", borderRadius: 6, boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}
          />
        ) : (
          <span style={{ ...META, fontSize: 11.5, color: "rgba(255,255,255,0.45)" }}>NO PREVIEW</span>
        )}
      </div>
      <aside
        style={{
          width: narrow ? "100%" : 360,
          flexShrink: 0,
          background: C.viewerRail,
          borderLeft: narrow ? "none" : "1px solid rgba(255,255,255,0.07)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <div style={{ padding: "28px 26px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.4px", color: C.gold }}>DESCRIPTION</span>
          <p style={{ marginTop: 14, fontSize: 18.4, lineHeight: 1.45, fontWeight: 600 }}>
            {photo.description ?? "No description"}
          </p>
        </div>
        {facts.length > 0 && (
          <div style={{ padding: "22px 26px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.4px", color: "rgba(255,255,255,0.45)" }}>GENERAL</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
              {facts.map((fact) => (
                <div key={fact.label}>
                  <div style={{ fontSize: 13.8, fontWeight: 500, color: "rgba(255,255,255,0.45)" }}>{fact.label}</div>
                  <div style={{ fontSize: 16.1, fontWeight: 600, marginTop: 3, overflowWrap: "anywhere" }}>{fact.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginTop: "auto", padding: "16px 26px 22px" }}>
          <button
            className="kb"
            data-testid="photo-viewer-download"
            disabled={!downloadSource || downloading}
            onClick={async () => {
              if (!downloadSource || downloading) return;
              const attempt = ++downloadAttempt.current;
              setDownloading(true);
              setDownloadFailed(false);
              const ok = await downloadFile(downloadSource, suggestedName(photo, index), {
                authenticated: !!downloadUrl,
              });
              if (attempt !== downloadAttempt.current) return;
              setDownloadFailed(!ok);
              setDownloading(false);
            }}
            style={{
              width: "100%",
              height: 44,
              borderRadius: 12,
              border: `1px solid ${downloadFailed ? C.red : "rgba(255,255,255,0.18)"}`,
              background: "transparent",
              color: downloadFailed ? C.red : C.surface,
              fontSize: 15.52,
              fontWeight: 700,
              cursor: downloading ? "progress" : downloadSource ? "pointer" : "not-allowed",
              opacity: downloadSource ? 1 : 0.45,
            }}
          >
            {downloading ? "Preparing download…" : downloadFailed ? "Download failed — try again" : "Download"}
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
